import { Spinner } from '@Common/Spinner';
import { CheckIcon } from '@heroicons/react/24/outline';
import { providerIcon } from 'appConstants';
import { returnSubscriptionPeriod } from 'helpers/validation';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { useGetPaymentsList } from 'store/subscription/actions';
import { SubscriptionPriceType } from 'store/subscription/types';

interface UpdateSubscriptionStepProps {
  price: SubscriptionPriceType | null;
  activePrice: SubscriptionPriceType | undefined;
}

export const UpdateSubscriptionStep = ({
  activePrice,
  price,
}: UpdateSubscriptionStepProps) => {
  const { data: paymentsMethod, isPending: isPaymentsPending } = useGetPaymentsList();
  const defaultPayment = paymentsMethod?.find((payment) => payment.is_default);

  return (
    <div className="flex flex-col w-full">
      <div className="flex items-center justify-between overflow-auto mb-5">
        <div className="flex flex-col space-y-3 p-3 rounded-lg bg-gray-100 w-5/12">
          <div>
            <FormattedMessage id="currentTier" />
          </div>

          <div className="text-gray-500 text-lg">
            {activePrice ? (
              returnSubscriptionPeriod(activePrice.nickname)
            ) : (
              <FormattedMessage id="canceled" />
            )}
            {}
          </div>
          <p className="mt-6 flex items-baseline gap-x-1">
            <span className="text-4xl font-bold tracking-tight text-gray-900">
              {activePrice ? (
                <FormattedNumber
                  style="currency"
                  currency={activePrice.currency.toUpperCase() || 'EUR'}
                  value={+activePrice.unit_amount / 100}
                  maximumFractionDigits={0}
                />
              ) : (
                <FormattedMessage id="free" />
              )}
            </span>
            {activePrice && (
              <span className="text-sm font-semibold leading-6 text-gray-600">
                / {activePrice?.interval}
              </span>
            )}
          </p>
        </div>
        <div className="text-lg font-medium">{'>'}</div>
        <div className="flex flex-col space-y-3 p-3 rounded-lg bg-gray-100  w-5/12">
          <div>
            <FormattedMessage id="newTier" />
          </div>
          <div className="text-gray-500 text-lg">
            {returnSubscriptionPeriod(price?.nickname || '')}
          </div>
          <p className="mt-6 flex items-baseline gap-x-1">
            <span className="text-4xl font-bold tracking-tight text-gray-900">
              <FormattedNumber
                style="currency"
                currency={price?.currency.toUpperCase()}
                value={+(price?.unit_amount || 0) / 100}
                maximumFractionDigits={0}
              />
            </span>
            <span className="text-sm font-semibold leading-6 text-gray-600">
              / {price?.interval}
            </span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 border-b pb-3 mb-3">
        <div className="w-full">
          <div className="text-lg font-medium">
            <FormattedMessage id="whatHappenedNext" />
          </div>
          <ul className="space-y-2 text-sm leading-6 text-gray-800 mt-2">
            <li className="flex gap-x-3">
              <CheckIcon
                className="h-6 w-5 flex-none text-indigo-600"
                aria-hidden="true"
              />
              <FormattedMessage
                id="preparationCreatedRightAway"
                values={{
                  name: price?.nickname,
                }}
              />
            </li>
          </ul>
        </div>
        <div className="w-full">
          <div className="text-lg font-medium mb-2">
            <FormattedMessage id="payment" />
          </div>
          {isPaymentsPending ? (
            <Spinner color="bg-primary" />
          ) : (
            <div className="flex w-full items-center justify-between">
              <div className="flex flex-col items-start">
                <div className="flex items-center text-base text-gray-600 text-left truncate">
                  {providerIcon[
                    defaultPayment?.card.brand.toLocaleLowerCase() as keyof typeof providerIcon
                  ]?.baseIcon || null}
                  <div className="ml-2 truncate">
                    *** *** *** {defaultPayment?.card.last4}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="w-full text-gray-700 text-sm">
        <FormattedMessage
          id="updatePlanTerms"
          values={{
            tierTitle: returnSubscriptionPeriod(price?.nickname || ''),
            termsLink: (
              <a
                className="text-primary underline"
                rel="noreferrer"
                target="_blank"
                href="https://www.digital-menu.ai/terms"
              >
                DigitalMenu.AI terms
              </a>
            ),
          }}
        />
      </div>
    </div>
  );
};
